import { computed, ComputedRef } from 'vue'
import { dateUtils } from './../utils/date-utils'
import {
  parse,
  isValid,
  isAfter,
  isBefore,
  isWithinInterval,
  format,
} from 'date-fns'
import { useStore } from 'vuex'
import { ConfigGetters } from '@/store/configs-store'

const RAW_DATE_FORMAT = 'yyyyMMdd'

export function useDateUtils(
  minDate: ComputedRef<Date>,
  maxDate: ComputedRef<Date>,
) {
  const store = useStore()
  const getLocale = computed(() => store.getters[ConfigGetters.GET_LOCALE])
  const getDateFormat = computed(
    () => store.getters[ConfigGetters.GET_DATE_FORMAT],
  )

  const rawToDisplay = (rawDateStr: string): string => {
    if (!rawDateStr) {
      return ''
    }

    try {
      const dateObj = parse(rawDateStr, RAW_DATE_FORMAT, new Date())
      const formatToUse = getDateFormat.value?.replace(/-/g, '/')
      return format(dateObj, formatToUse)
    } catch (error) {
      return ''
    }
  }

  const isValidDate = (dateObj: Date): boolean => {
    if (!isValid(dateObj)) {
      return false
    }

    const normalized = dateUtils.normalizeDate(dateObj)

    if (minDate.value && maxDate.value) {
      return isWithinInterval(normalized, {
        start: minDate.value,
        end: maxDate.value,
      })
    } else if (minDate.value) {
      return isAfter(normalized, minDate.value)
    } else if (maxDate.value) {
      return isBefore(normalized, maxDate.value)
    }

    return true
  }

  /**
   * Formats a date string according to the configured date format
   * @param dateString string that can be casted to Date with new Date
   * @returns string date in the configured format
   */
  const formatDateString = (dateString: string) => {
    const formatToUse = getDateFormat.value?.replace(/-/g, '/')
    return format(new Date(dateString), formatToUse)
  }

  /**
   * Formats a date string to dd/MM/yyyy, HH:mm
   * @param dateString string that can be casted to Date with new Date
   * @returns string date dd/MM/yyyy, HH:mm
   */
  const formatFullDate = (dateString: string): string => {
    const formatToUse = getDateFormat.value?.replace(/-/g, '/')
    return format(new Date(dateString), formatToUse + ', HH:mm')
  }

  return {
    ...dateUtils,
    rawToDisplay,
    isValidDate,
    getLocale,
    getDateFormat,
    formatFullDate,
    formatDateString,
  }
}
